// constants
import { EMPLOYEES_QUERY_KEY } from '../RosterPlan.consts';

// types
import type { QueryClient } from 'react-query';
import type { EmployeeDutyData } from 'RestAPIs/cost-centers/employees/types';
import type { LiveUpdateMessageFilter } from '../RosterPlan.types';

/** The bridge frame around an hours notification: `{ type: 'rec', body: { type, body: periodHours } }`. */
type HoursMessageFrame = { body?: { body?: { waId?: number } } };

/** Built once per employees response rather than once per message of a burst. */
const workAgreementIdsByResponse = new WeakMap<
  EmployeeDutyData[],
  Set<number>
>();

const getWorkAgreementIds = (employees: EmployeeDutyData[]) => {
  let ids = workAgreementIdsByResponse.get(employees);
  if (!ids) {
    ids = new Set(employees.map((employee) => employee.workAgreementId));
    workAgreementIdsByResponse.set(employees, ids);
  }
  return ids;
};

/** `null` for a frame without one, such as an error frame. */
const getWorkAgreementId = (message: unknown) => {
  const waId = (message as HoursMessageFrame | undefined)?.body?.body?.waId;
  return typeof waId === 'number' && Number.isFinite(waId) ? waId : null;
};

const hasFailedLoad = (queryClient: QueryClient) =>
  queryClient
    .getQueryCache()
    .findAll([EMPLOYEES_QUERY_KEY])
    .some((query) => query.state.status === 'error');

const getLoadedResponses = (queryClient: QueryClient) =>
  queryClient
    .getQueriesData<EmployeeDutyData[] | undefined>([EMPLOYEES_QUERY_KEY])
    .map(([, employees]) => employees)
    .filter((employees): employees is EmployeeDutyData[] =>
      Array.isArray(employees)
    );

/**
 * Passes on only the hours messages about an employee in the loaded employees response.
 *
 * `HoursCalculationNotification` publishes a recalculated work agreement on every month of its year,
 * so without this, one duty saved anywhere in the tenant would refetch every open roster of that
 * year. The message names the work agreement (`waId`), which the loaded rows carry as
 * `workAgreementId`. The check is against the response, before the filter popper narrows it, so a
 * filtered-out employee still counts.
 *
 * While nothing is loaded — every navigation, as the app keeps no cache — a message cannot be
 * checked yet, and the fetch in flight may predate it. Its `waId` is kept until the response lands
 * and checked then; a failed load drops them. After a failed load a message cannot be checked at
 * all, so it asks for a retry instead; `createCoalescedInvalidator` holds that to once per
 * `LIVE_UPDATE_ERROR_GAP_MS`, so tenant-wide traffic cannot turn it into a retry loop.
 *
 * Known gap: an employee who is not in the response yet — a duty planned in from another cost
 * centre — does not count, and shows on the next load.
 */
export const createHoursMessageFilter = (
  queryClient: QueryClient,
  onRelevant: () => void
): LiveUpdateMessageFilter => {
  const waIdsWhileLoading = new Set<number>();

  const settleWhileLoading = (employees: unknown) => {
    const ids = Array.isArray(employees)
      ? getWorkAgreementIds(employees as EmployeeDutyData[])
      : null;
    const isHit = !!ids && [...waIdsWhileLoading].some((id) => ids.has(id));
    waIdsWhileLoading.clear();
    if (isHit) onRelevant();
  };

  const stopWatching = queryClient.getQueryCache().subscribe((event) => {
    if (waIdsWhileLoading.size === 0 || event?.type !== 'queryUpdated') return;
    if (event.query.queryKey[0] !== EMPLOYEES_QUERY_KEY) return;
    if (event.action.type === 'success')
      settleWhileLoading(event.query.state.data);
    if (event.action.type === 'error') waIdsWhileLoading.clear();
  });

  return {
    handle: (message) => {
      const waId = getWorkAgreementId(message);
      if (waId === null) return;

      const responses = getLoadedResponses(queryClient);
      if (responses.length === 0) {
        // Only a load in flight can settle what is held.
        if (queryClient.isFetching([EMPLOYEES_QUERY_KEY]) > 0) {
          waIdsWhileLoading.add(waId);
          return;
        }
        // With nothing loading the load failed, and there is nothing to check against. Holding
        // would replay the ids against whatever loads next, a response they predate; instead the
        // message asks for a retry, which the invalidator paces at its failed-load gap.
        if (hasFailedLoad(queryClient)) onRelevant();
        return;
      }
      if (
        responses.some((employees) => getWorkAgreementIds(employees).has(waId))
      ) {
        onRelevant();
      }
    },
    dispose: () => {
      stopWatching();
      waIdsWhileLoading.clear();
    }
  };
};
